import { Server } from '@healthblocks-io/core'
import { useContext } from 'react'

import { environment, viteEnvironment } from '@/config/environment'
import { useFeatureFlag } from '@/utils/feature-flag'

import type { ServerInfo } from '@healthblocks-io/core'

export const tenantSlugs = ['azmm', 'olvw'] as const
export type TenantSlug = (typeof tenantSlugs)[number]
/**
 * If a tenant route would start with one of these prefixes, it may behave unexpectedly
 * /azmmexample will not redirect to /azmm/azmmexample
 */
export const tenantPrefixes = ['/azmm', '/olvw']
export const defaultTenantPrefix = '/azmm'

/**
 * Returns true if the given path is considered a tenant-specific route
 */
export function hasTenantPrefix(path: string) {
  return tenantPrefixes.some((prefix) => path.startsWith(prefix))
}

/** The combination of a tenant and an environment should match a server URL */
export const servers: ServerInfo[] = []
if (environment.env === 'production') {
  servers.push(
    {
      id: 'azmm_prd',
      api: 'https://api.mijnziekenhuis.be',
      integrationSynopsUrl: 'https://integration-synops.mijnziekenhuis.be',
      display: 'Maria Middelares met sites in\nGent, Deinze, Aalter en Gentbrugge',
      logo: '/img/Maria_Middelares.png',
      projectId: 2,
      tenant: 'azmm',
      environment: 'prd',
    },
    {
      id: 'olvw_prd',
      api: 'https://api-olvw.ziekenhuiswaregem.be',
      integrationSynopsUrl: 'https://synops-olvw.ziekenhuiswaregem.be',
      display: 'O.L.V. van Lourdes Waregem',
      logo: '/img/olv-waregem.png',
      projectId: 4,
      tenant: 'olvw',
      environment: 'prd',
    },
  )
} else if (window.location.hostname.includes('.qa.')) {
  servers.push({
    id: 'preview',
    api: window.location.origin.replace('//patient-', '//api-'),
    integrationSynopsUrl: window.location.origin.replace('//patient-', '//synops-'),
    display: 'Preview server',
    projectId: 1,
    environment: 'qa',
    tenant: 'azmm',
  })
} else
  switch (environment.env) {
    case 'local': {
      servers.push(
        {
          id: 'azmm_local',
          display: 'Maria Middelares Localhost',
          api: 'http://localhost:21111',
          integrationSynopsUrl: 'http://localhost:22000',
          projectId: 1,
          environment: 'local',
          logo: '/img/Maria_Middelares.png',
          tenant: 'azmm',
        },
        {
          id: 'olvw_local',
          api: 'http://localhost:21111',
          integrationSynopsUrl: 'http://localhost:22000',
          display: 'O.L.V. van Lourdes Waregem',
          logo: '/img/olv-waregem.png',
          projectId: 2,
          environment: 'local',
          tenant: 'olvw',
        },
      )

      break
    }
    case 'development': {
      servers.push(
        {
          id: 'azmm_dev',
          api: 'https://api-dev.mijnziekenhuis.be',
          integrationSynopsUrl: 'https://integration-synops-dev.mijnziekenhuis.be',
          display: 'Maria Middelares met sites in\nGent, Deinze, Aalter en Gentbrugge',
          logo: '/img/Maria_Middelares.png',
          projectId: 1,
          environment: 'dev',
          tenant: 'azmm',
        },
        // {
        //   id: 'olvw_dev',
        //   api: 'https://api-olvw-dev.mijnziekenhuis.be',
        //   integrationSynopsUrl: 'https://synops-olvw-dev.mijnziekenhuis.be',
        //   display: 'O.L.V. van Lourdes Waregem',
        //   logo: '/img/olvw-waregem.png',
        //   projectId: 1,
        //   environment: 'dev',
        //   tenant: 'olvw',
        // },
        {
          id: 'olvw_acc',
          api: 'https://api-olvw-acc.ziekenhuiswaregem.be',
          integrationSynopsUrl: 'https://synops-olvw-acc.ziekenhuiswaregem.be',
          display: 'O.L.V. van Lourdes Waregem',
          logo: '/img/olv-waregem.png',
          projectId: 3,
          environment: 'acc',
          tenant: 'olvw',
        },
      )

      break
    }
    case 'staging': {
      servers.push(
        {
          id: 'azmm_acc',
          api: 'https://api-acc.mijnziekenhuis.be',
          integrationSynopsUrl: 'https://integration-synops-acc.mijnziekenhuis.be',
          display: 'Maria Middelares met sites in\nGent, Deinze, Aalter en Gentbrugge',
          logo: '/img/Maria_Middelares.png',
          projectId: 1,
          environment: 'acc',
          tenant: 'azmm',
        },
        {
          id: 'olvw_acc',
          api: 'https://api-olvw-acc.ziekenhuiswaregem.be',
          integrationSynopsUrl: 'https://synops-olvw-acc.ziekenhuiswaregem.be',
          display: 'O.L.V. van Lourdes Waregem',
          logo: '/img/olv-waregem.png',
          projectId: 3,
          environment: 'acc',
          tenant: 'olvw',
        },
      )

      break
    }
    // No default
  }

export function useServer() {
  const { tenant } = useContext(Server)

  if (!tenant) {
    return undefined
  }

  return (
    servers.find((s) => s.tenant === tenant && s.environment === viteEnvironment) ||
    servers.find((s) => s.tenant === tenant)
  )
}

export function useServers() {
  const olvw = useFeatureFlag('tenant_olvw') // ?? '1'
  return olvw ? servers : servers.filter((s) => s.tenant !== 'olvw')
}
